import { useEffect } from "react";

import {
  getNextCutoff,
  isAddonProduct,
  isGroceryBasketType,
  isMealboxBasketType,
  isMealboxProduct,
  isStandaloneProduct,
} from "@chef/helpers";
import { language, PRODUCT_TYPE_IDS, STANDALONE_BASKET } from "@chef/constants";

import type { StandaloneBasketVariant } from "@chef/types/basket";

import {
  IDeviationProduct,
  selectDeviationProducts,
  updateAddonToDeviation,
  setAddonProducts,
  showNotification,
  createPayloadProductsArray,
  useUpdateAgreementBasketProductsMutation,
  useLazyBillingQuery,
  useLazyProductsByCategoriesQuery,
  useLazyProductCategoriesQuery,
} from "../..";
import { useAppDispatch, useAppSelector } from "../useApp";
import { useTrack } from "../useTrack";
import {
  getBasketProductQuantity,
  getStandaloneProductCategoryIdsFromProductCategories,
} from "../../helpers";
import { getUpdatedDeviationProducts } from "./utils";

// to ease up on type safety across the app I don't use a compound type here
// billing only requires the variant, while deviation requires week and year
type UseStandaloneBasketArgs = {
  variant?: StandaloneBasketVariant;
  week?: number;
  year?: number;
};

export const intl = (
  {
    no: {
      GENERIC_ERROR_MESSAGE: "Noe gikk galt, kontakt support",
    },
    se: {
      GENERIC_ERROR_MESSAGE: "Något gick fel, försök igen senare",
    },
    dk: {
      GENERIC_ERROR_MESSAGE: "Noget gik galt, kontakt support",
    },
  } as const
)[language];

export const useStandaloneBasket = (props: UseStandaloneBasketArgs) => {
  const { variant } = props;
  const { week: _week, year: _year } = getNextCutoff();
  const week = props.week ?? _week;
  const year = props.year ?? _year;
  const isDeviationVariant = variant === STANDALONE_BASKET.DEVIATION;
  const isBillingVariant = variant === STANDALONE_BASKET.BILLING;

  const dispatch = useAppDispatch();
  const track = useTrack();

  const [fetchBilling, { isLoading: isFetchingBilling, data: billingQuery }] =
    useLazyBillingQuery();

  const [fetchProductCategories] = useLazyProductCategoriesQuery();
  const [
    fetchProductsByCategoriesData,
    { data: standaloneProductsByCategoriesData },
  ] = useLazyProductsByCategoriesQuery();

  const deviationBasket =
    useAppSelector(selectDeviationProducts({ week, year })) || [];

  // TODO: FIX - temporary workaround for the selectAddonProducts selector forcing re-renders through faulty hydration
  const billingProducts = useAppSelector((state) => state.basket.addonProducts);
  const billingBasket = billingProducts.filter((product) =>
    (
      [
        PRODUCT_TYPE_IDS.STANDALONE,
        PRODUCT_TYPE_IDS.ADDON,
        PRODUCT_TYPE_IDS.LIMITED_QUANTITY,
      ] as string[]
    ).includes(product.productTypeId),
  );

  const basket = isBillingVariant ? billingBasket : deviationBasket;

  useEffect(() => {
    if (!isDeviationVariant) {
      return;
    }

    (async () => {
      await fetchBilling(undefined, true);

      const productCategories = await fetchProductCategories(
        undefined,
        true,
      ).unwrap();

      fetchProductsByCategoriesData(
        {
          categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
            productCategories.productCategories || [],
          ),
          week: week,
          year: year,
        },
        true,
      );
    })();
  }, []);

  const [updateAgreementBasketProducts, { isLoading: isSubmittingBilling }] =
    useUpdateAgreementBasketProductsMutation();

  const billing = billingQuery?.billing;
  const subscribedProducts = billing?.baskets
    .find(isGroceryBasketType)
    ?.basketProducts.filter(
      (basketProduct) =>
        isStandaloneProduct(basketProduct.variation.product) ||
        isAddonProduct(basketProduct.variation.product),
    );

  const isBillingLoading = isFetchingBilling || isSubmittingBilling;

  const addToBasket = (product: Omit<IDeviationProduct, "quantity">) => {
    const currentBasketQuantity = getBasketProductQuantity(
      basket,
      product.productId,
      product.variationId,
    );

    const newBasketProduct = {
      ...product,
      quantity: currentBasketQuantity + 1,
    };

    addOrRemoveProduct(newBasketProduct);
  };

  const removeFromBasket = (product: Omit<IDeviationProduct, "quantity">) => {
    const currentBasketQuantity = getBasketProductQuantity(
      basket,
      product.productId,
      product.variationId,
    );

    const newBasketProduct = {
      ...product,
      quantity: currentBasketQuantity - 1,
    };

    addOrRemoveProduct(newBasketProduct);
  };

  const addOrRemoveProduct = (product: IDeviationProduct) => {
    if (isDeviationVariant) {
      dispatch(updateAddonToDeviation({ week: week, year: year, product }));
    }

    if (isBillingVariant) {
      const updatedBasket = getUpdatedDeviationProducts(product, basket);
      dispatch(setAddonProducts(updatedBasket));
    }
  };

  const addProductToSubscription = async (
    updatedProduct: IDeviationProduct,
  ) => {
    if (!isDeviationVariant) {
      return;
    }

    let addonProducts =
      subscribedProducts?.map((p) => {
        return { variationId: p.variationId, quantity: p.quantity };
      }) || [];

    const shouldBeRemoved = updatedProduct.quantity === 0;

    const oldQuantity =
      addonProducts.find(
        (basketProduct) =>
          basketProduct.variationId === updatedProduct.variationId,
      )?.quantity || 0;

    const productExistsInBasket = oldQuantity > 0;

    if (shouldBeRemoved) {
      addonProducts = addonProducts.filter(
        (p) => p.variationId !== updatedProduct.variationId,
      );
    } else if (!productExistsInBasket) {
      addonProducts = [
        ...addonProducts,
        {
          variationId: updatedProduct.variationId,
          quantity: updatedProduct.quantity,
        },
      ];
    } else {
      addonProducts = addonProducts.map((p) => {
        if (p.variationId !== updatedProduct.variationId) {
          return p;
        }

        return {
          variationId: updatedProduct.variationId,
          quantity: updatedProduct.quantity,
        };
      });
    }

    if (!billing) {
      console.error("submit called before billing loaded");
      return;
    }

    const defaultBasket = billing.baskets?.find(isMealboxBasketType);

    const mealboxBasketProduct = defaultBasket?.basketProducts.find((bp) =>
      isMealboxProduct(bp.variation.product),
    );

    const groceryBasket = billing.baskets?.find(isGroceryBasketType);

    if (!mealboxBasketProduct) {
      console.error(
        "Attempted to update addons subscription without existing mealboxProduct in billing",
      );
      showNotification({
        type: "error",
        message: intl.GENERIC_ERROR_MESSAGE,
      });
      return;
    }

    await updateAgreementBasketProducts({
      basketId: groceryBasket?.basketId,
      products: addonProducts.map(({ variationId, quantity }) => ({
        variationId,
        quantity,
        deliveryWeekType: 10,
        isExtra: false,
      })),
    }).unwrap();

    const updatedBasket = getUpdatedDeviationProducts(updatedProduct, basket);

    dispatch(setAddonProducts(updatedBasket));

    if (!updatedProduct) {
      return;
    }

    const productData = createPayloadProductsArray(
      [updatedProduct],
      undefined,
      standaloneProductsByCategoriesData?.productsByCategories,
    )?.[0];

    if (!productData) {
      return;
    }

    track("subscriptionAddonsChanged", {
      affiliation: "Frontend process",
      source: "weekEditor",
      product: {
        category: productData.category,
        product_id: productData.product_id,
        name: productData.name,
        price: productData.price,
        category_id: productData.category_id,
        variant_id: productData.variant_id,
        variant: productData.variant,
      },
      newQuantity: updatedProduct.quantity,
      oldQuantity,
    });
  };

  return {
    basket,
    addOrRemoveProduct,
    addToBasket,
    removeFromBasket,
    addProductToSubscription,
    subscribedProducts,
    isBillingLoading,
  };
};
